import { useEffect, useState } from "react";
import { ChevronRight, Database, Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import AddColumnForm from "@/components/admin/AddColumnForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function ColumnRow({ db, table, col, onSaved }) {
  const [name, setName] = useState(col.name);
  const [busy, setBusy] = useState(false);
  useEffect(() => setName(col.name), [col.name]);
  const remove = async () => {
    if (!window.confirm(`Remove column "${col.name}" and its values from every record in ${table}?`)) return;
    setBusy(true);
    try { onSaved((await api.delete(`/databases/${db.id}/tables/${encodeURIComponent(table)}/columns/${encodeURIComponent(col.name)}`)).data); toast.success(`Removed "${col.name}"`); }
    catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };
  const save = async () => {
    setBusy(true);
    try { onSaved((await api.patch(`/databases/${db.id}/tables/${encodeURIComponent(table)}/columns`, { old: col.name, new: name })).data); toast.success(`Renamed to "${name.trim()}"`); }
    catch (e) { toast.error(formatError(e)); setName(col.name); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-2">
      <Input className="h-8" value={name} maxLength={64} onChange={(e) => setName(e.target.value)} data-testid={`col-input-${db.id}-${col.name}`} />
      <span className="text-[10px] font-mono uppercase text-muted-foreground w-14">{col.type}</span>
      <Button size="sm" variant="outline" disabled={busy || !name.trim() || name === col.name} onClick={save} data-testid={`col-save-${db.id}-${col.name}`}>{busy ? <Loader2 size={13} className="animate-spin" /> : "Rename"}</Button>
      {col.custom && <Button size="sm" variant="ghost" className="text-destructive px-2" disabled={busy} onClick={remove} title="Remove column" data-testid={`col-remove-${db.id}-${col.name}`}><Trash2 size={13} /></Button>}
    </div>
  );
}

function DbCard({ d, parent, onSaved }) {
  const [v, setV] = useState({ name: d.name, description: d.description || "" });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => setV({ name: d.name, description: d.description || "" }), [d]);
  const dirty = v.name !== d.name || v.description !== (d.description || "");
  const save = async () => {
    setBusy(true);
    try { onSaved((await api.patch(`/databases/${d.id}`, v)).data); toast.success("Database details saved"); } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };
  const editable = d.tables.filter((t) => !t.name.startsWith("_"));
  return (
    <div className={`border rounded-lg bg-card p-4 space-y-3 ${parent ? "ml-6 border-l-4 border-l-primary/40" : ""}`} data-testid={`admin-db-${d.id}`}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground"><Database size={13} /> {parent ? `Sub database of ${parent.name}` : d.filename} · {d.tables.reduce((s, t) => s + t.row_count, 0)} records</div>
      <div className="grid sm:grid-cols-[1fr_auto] gap-2">
        <Input value={v.name} maxLength={80} onChange={(e) => setV({ ...v, name: e.target.value })} data-testid={`db-name-input-${d.id}`} />
        <Button onClick={save} disabled={busy || !dirty || !v.name.trim()} data-testid={`db-save-${d.id}`}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</Button>
      </div>
      <Textarea rows={2} maxLength={1000} placeholder="Info / notes about this database" value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} data-testid={`db-desc-input-${d.id}`} />
      {editable.length > 0 && <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-sm text-primary" data-testid={`db-columns-toggle-${d.id}`}>
        <ChevronRight size={14} className={`transition-transform ${open ? "rotate-90" : ""}`} /> Columns: rename, add or remove ({editable.length} table{editable.length === 1 ? "" : "s"})
      </button>}
      {open && editable.map((t) => (
        <div key={t.name} className="space-y-2 border-t pt-3">
          <p className="text-sm font-medium">{t.name}</p>
          <div className="grid md:grid-cols-2 gap-2">{t.columns.map((c) => <ColumnRow key={c.name} db={d} table={t.name} col={c} onSaved={onSaved} />)}</div>
          <AddColumnForm dbId={d.id} table={t.name} onAdded={onSaved} testPrefix={`add-col-${d.id}-${t.name.replace(/\s+/g, "-")}`} />
        </div>
      ))}
    </div>
  );
}

export default function DatabasesTab() {
  const [dbs, setDbs] = useState(null);
  const load = () => api.get("/databases").then((r) => setDbs(r.data));
  useEffect(() => { load(); }, []);
  const onSaved = (d) => { setDbs((p) => p.map((x) => (x.id === d.id ? { ...x, ...d } : x))); window.dispatchEvent(new Event("databases-changed")); };
  if (!dbs) return <Loader2 className="animate-spin text-primary" />;
  const tops = dbs.filter((d) => !d.parent_db_id || !dbs.some((p) => p.id === d.parent_db_id));
  return (
    <div className="space-y-3 max-w-4xl" data-testid="databases-tab">
      <p className="text-sm text-muted-foreground">Rename databases and sub databases, add notes, and rename columns. Renaming a column updates every record in that table.</p>
      {tops.map((d) => (
        <div key={d.id} className="space-y-3">
          <DbCard d={d} onSaved={onSaved} />
          {dbs.filter((c) => c.parent_db_id === d.id).map((c) => <DbCard key={c.id} d={c} parent={d} onSaved={onSaved} />)}
        </div>
      ))}
      {dbs.length === 0 && <p className="text-muted-foreground">No databases yet.</p>}
    </div>
  );
}
